/**
 * @file BlockList.tsx
 * @description Список блоков страницы в структуре конструктора (phase19.16
 * Р1-6а; Р2 — uiux-design-r2 §5, §6): перетаскивание (dnd-kit), показ,
 * «⋯» (Дублировать · Выше · Ниже · Удалить), «+ блок сюда» между блоками,
 * неподдерживаемый блок из данных — только удалить. Общий для обычной страницы и мест
 * секций на экране «Блог».
 *
 * phase19.16 Р2а (шаблон страницы товара): под строкой блока — его
 * элементы (`renderBelow`), у блока с обязательными элементами скрытие
 * недоступно (`toggleLocked`), «Дублировать» — с пояснением.
 */

import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DND_ACCESSIBILITY_RU } from "../../../lib/site-builder/dnd-ru";
import { blocksFullText } from "../../../lib/site-builder/block-limits";
import { ArrowDown, ArrowUp, Copy, Trash2 } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { SortableBlockRow } from "../../site-pages/SortableBlockRow";
import { isSectionBlock } from "../../site-pages/block-meta";
import { useBuilder } from "../builder-context";
import { AddBlockMenu } from "./AddBlockMenu";
import type { SitePageBlock } from "../../../lib/api-site-pages";

interface Props {
  blocks: SitePageBlock[];
  /** Выбрать блок (на экране «Блог» — с переходом на страницу места). */
  onSelect: (block: SitePageBlock) => void;
  selectedId: string | null;
  /** Действия доступны только на открытой странице блока. */
  editable: boolean;
  /** Под строкой блока (элементы блока шаблона). */
  renderBelow?: (block: SitePageBlock) => ReactNode;
  /** Почему блок нельзя скрыть; `null` — можно. */
  toggleLocked?: (block: SitePageBlock) => string | null;
  /** Пояснение у «Дублировать» (шаблон: копия без элементов товара). */
  duplicateHint?: string;
  /** Р3а: метки черновика по id блока («изменён» / «новый»). */
  marks?: Readonly<Record<string, string>>;
}

export function BlockList({ blocks, onSelect, selectedId, editable, renderBelow, toggleLocked, duplicateHint, marks }: Props) {
  const b = useBuilder();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // У шаблона товара свой лимит блоков (SB64, Р2а).
  const full = blocksFullText({ kind: b.pages.page?.kind, blocks }) !== null;

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!editable || !over || active.id === over.id) return;
    const from = blocks.findIndex((x) => x.id === active.id);
    const to = blocks.findIndex((x) => x.id === over.id);
    if (from >= 0 && to >= 0) void b.blocks.reorder(arrayMove(blocks, from, to));
  }

  async function duplicate(block: SitePageBlock) {
    const id = await b.blocks.duplicate(block);
    if (id) await b.select({ sel: { kind: "block", id }, panel: null });
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={DND_ACCESSIBILITY_RU}>
      <SortableContext items={blocks.map((x) => x.id)} strategy={verticalListSortingStrategy}>
        <ul className="flex flex-col gap-0.5">
          {editable ? (
            <li>
              <AddBlockMenu inline after={null} disabled={full} />
            </li>
          ) : null}
          {blocks.map((block, index) => (
            <Fragment key={block.id}>
              <SortableBlockRow
                block={block}
                isSelected={block.id === selectedId}
                readOnly={!editable}
                onDelete={editable ? (x) => void b.blocks.remove(x) : undefined}
                onSelect={onSelect}
                onToggle={(x, on) => void b.blocks.toggle(x, on)}
                toggleLocked={toggleLocked?.(block) ?? null}
                mark={marks?.[block.id] ?? null}
                actions={
                  editable
                    ? [
                        ...(!isSectionBlock(block) ? [] : [{ key: "dup", label: "Дублировать", icon: Copy, disabled: full, hint: duplicateHint, run: () => void duplicate(block) }]),
                        { key: "up", label: "Выше", icon: ArrowUp, disabled: index === 0, run: () => void b.blocks.move(block, -1) },
                        { key: "down", label: "Ниже", icon: ArrowDown, disabled: index === blocks.length - 1, run: () => void b.blocks.move(block, 1) },
                        { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => void b.blocks.remove(block) },
                      ]
                    : []
                }
              />
              {renderBelow ? <li className="pl-8">{renderBelow(block)}</li> : null}
              {editable && index < blocks.length - 1 ? (
                <li>
                  <AddBlockMenu inline after={block.id} disabled={full} />
                </li>
              ) : null}
            </Fragment>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
